import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Loader2, RefreshCw, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { DashboardLayout } from '../components/DashboardLayout';
import { reportsAPI } from '../lib/api';
import { usePortfolioPrefsStore } from '../store';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/errors';
import { CurrencyToggle } from '../components/CurrencyToggle';
import { convertAmount } from '../lib/currencyConvert';
import { BROSDashboardPnlTab } from './BROSDashboardPnlTab';

// ── Period options ────────────────────────────────────────────────────

const PERIODS = [
  { value: 'ytd', label: 'YTD' },
  { value: 'last_12m', label: 'Last 12M' },
  { value: 'all', label: 'All Time' },
];

// ── Shared pill controls ──────────────────────────────────────────────

// Props destructured in body (babel-metadata-plugin workaround).
function PeriodPills(props) {
  const { period, onPeriodChange } = props;
  return (
    <div className="flex gap-0.5 bg-muted rounded-md p-0.5">
      {PERIODS.map((p) => (
        <button
          key={p.value}
          onClick={() => onPeriodChange(p.value)}
          className={cn(
            'px-3 py-1.5 text-sm font-medium rounded transition-all',
            period === p.value
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          )}
        >
          {p.label}
        </button>
      ))}
    </div>
  );
}

// ── High-margin caveat ────────────────────────────────────────────────

// Props destructured in body (babel-metadata-plugin workaround).
function HighMarginCaveat(props) {
  const { marginPct } = props;
  if (marginPct == null || marginPct <= 60) return null;
  return (
    <Card className="border-amber-300 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-700">
      <CardContent className="pt-4">
        <div className="flex items-start gap-2">
          <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
          <div>
            <p className="font-medium text-amber-800 dark:text-amber-200 mb-1">
              Margin appears high ({marginPct.toFixed(0)}%)
            </p>
            <p className="text-sm text-amber-700 dark:text-amber-300">
              If supplier costs have not been fully entered for this period, the margin figure may be overstated.
              Review the Costs page to verify supplier cost coverage.
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Main page ─────────────────────────────────────────────────────────

export function BROSProfitLossPage() {
  const [period, setPeriod] = useState('ytd');
  const { displayCurrency } = usePortfolioPrefsStore();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  const fetchData = useCallback(async (p) => {
    setLoading(true);
    try {
      const res = await reportsAPI.brosDashboard(p);
      setData(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(period);
  }, [period, fetchData]);

  // Currency conversion. Page amounts are natively GBP; delegate to the
  // shared convertAmount helper (handles GBP no-op and any fx_rates-supported
  // currency, not just EUR).
  const dc = displayCurrency;
  const cx = useCallback(
    (v) => convertAmount(v, 'GBP', dc, data?.fx_rates),
    [dc, data]
  );

  const handleRefresh = useCallback(() => {
    fetchData(period);
    setRefreshKey((k) => k + 1);
  }, [period, fetchData]);

  const marginPct = data?.kpis?.margin_pct ?? null;

  // Initial full-page loader
  if (loading && !data) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-[50vh]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* ── Header ── */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Profit & Loss</h1>
            <p className="text-muted-foreground mt-1">
              BR OS B2B gross margin: client revenue minus supplier costs
              {data?.period_label && ` \u00B7 ${data.period_label}`}
              {data?.date_range && (
                <span className="ml-1">
                  ({data.date_range.start} to {data.date_range.end})
                </span>
              )}
              {' \u00B7 '}{dc}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <PeriodPills period={period} onPeriodChange={setPeriod} />
            <CurrencyToggle />
            <Button variant="outline" size="icon" onClick={handleRefresh} disabled={loading}>
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>

        {/* ── High-margin caveat ── */}
        <HighMarginCaveat marginPct={marginPct} />

        {/* ── P&L content (reused from dashboard tab) ── */}
        <BROSDashboardPnlTab
          data={data}
          loading={loading}
          dc={dc}
          cx={cx}
          period={period}
          refreshKey={refreshKey}
        />
      </div>
    </DashboardLayout>
  );
}
